import {
  BaseballIncidentId,
  Confirmation,
} from '@collector/sportsapi-client-legacy'

import {
  IncidentQueuePayloadConfig,
  IncidentsQueuePayloadConfig,
} from '../../../configuration'
import {
  getAddIncidentContext,
  getThrowPitcherPayload,
  setParticipantTeamId,
  setSubParticipant,
} from './utils'

const breakAfterInnings: BaseballIncidentId[] = [
  BaseballIncidentId.BreakAfterFirstInning,
  BaseballIncidentId.BreakAfterSecondInning,
  BaseballIncidentId.BreakAfterThirdInning,
  BaseballIncidentId.BreakAfterFourthInning,
  BaseballIncidentId.BreakAfterFifthInning,
  BaseballIncidentId.BreakAfterSixthInning,
  BaseballIncidentId.BreakAfterSeventhInning,
  BaseballIncidentId.BreakAfterEighthInning,
  BaseballIncidentId.BreakAfterNinthInning,
  BaseballIncidentId.BreakAfterTenthInning,
  BaseballIncidentId.BreakAfterEleventhInning,
  BaseballIncidentId.BreakAfterTwelfthInning,
  BaseballIncidentId.BreakAfterThirteenthInning,
  BaseballIncidentId.BreakAfterFourteenthInning,
  BaseballIncidentId.BreakAfterFifteenthInning,
  BaseballIncidentId.BreakAfterSixteenthInning,
  BaseballIncidentId.BreakAfterSeventeenthInning,
  BaseballIncidentId.BreakAfterEighteenthInning,
  BaseballIncidentId.BreakAfterNineteenthInning,
  BaseballIncidentId.BreakAfterTwentiethInning,
  BaseballIncidentId.BreakAfterTwentyFirstInning,
  BaseballIncidentId.BreakAfterTwentySecondInning,
]

const sharedBreakAfterIncidentConfig: IncidentQueuePayloadConfig = {
  addIncident: (payload, queue) => {
    queue.addIncident({
      ...payload,
      incident_id: BaseballIncidentId.BreakAfter,
    })
    queue.addIncident(payload)
    queue.addIncident({
      ...payload,
      incident_id: BaseballIncidentId.ResetStats,
    })
  },
}

export const incidentsQueuePayloadConfig: IncidentsQueuePayloadConfig<BaseballIncidentId> =
  {
    ...Object.fromEntries(
      breakAfterInnings.map((id) => [id, sharedBreakAfterIncidentConfig]),
    ),
    [BaseballIncidentId.FinishedRegularTime]: {
      addIncident: (payload, queue) => {
        queue.addIncident(payload)
        queue.addIncident({
          ...payload,
          incident_id: BaseballIncidentId.ResetStats,
        })
      },
    },
    [BaseballIncidentId.FinishedAfterExtraInning]: {
      addIncident: (payload, queue) => {
        queue.addIncident(payload)
        queue.addIncident({
          ...payload,
          incident_id: BaseballIncidentId.ResetStats,
        })
      },
    },
    [BaseballIncidentId.Run]: {
      addIncident: (payload, queue, metadata) => {
        const { participant, currentBatter } = getAddIncidentContext(
          metadata,
          'batting',
          payload,
        )

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'secondary_assistant', payload)

        queue.addIncident({
          ...payload,
          confirmation: Confirmation.ToBeDone,
        })
      },
    },
    [BaseballIncidentId.HomeRun]: {
      addIncident: (payload, queue, metadata) => {
        const {
          participant,
          oppositeParticipant,
          currentBatter,
          currentPitcher,
        } = getAddIncidentContext(metadata, 'batting', payload)

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'participant', payload)

        const throwPitcherPayload = getThrowPitcherPayload(
          oppositeParticipant,
          payload.status_id,
        )
        setSubParticipant(currentPitcher, 'participant', throwPitcherPayload)

        queue.addIncident(throwPitcherPayload)
        queue.addIncident({
          ...payload,
          incident_id: BaseballIncidentId.Hit,
        })
        queue.addIncident({
          ...payload,
          confirmation: Confirmation.ToBeDone,
        })
      },
    },
    [BaseballIncidentId.Hit]: {
      addIncident: (payload, queue, metadata) => {
        const {
          participant,
          oppositeParticipant,
          currentBatter,
          currentPitcher,
        } = getAddIncidentContext(metadata, 'batting', payload)

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'participant', payload)

        const throwPitcherPayload = getThrowPitcherPayload(
          oppositeParticipant,
          payload.status_id,
        )
        setSubParticipant(currentPitcher, 'participant', throwPitcherPayload)

        queue.addIncident(throwPitcherPayload)
        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.Strikeout]: {
      addIncident: (payload, queue, metadata) => {
        const { participant, currentBatter } = getAddIncidentContext(
          metadata,
          'batting',
          payload,
        )

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'participant', payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.Out]: {
      addIncident: (payload, queue, metadata) => {
        const { participant, oppositeParticipant, currentPitcher } =
          getAddIncidentContext(metadata, 'batting', payload)

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentPitcher, 'assistant', payload)

        const throwPitcherPayload = getThrowPitcherPayload(
          oppositeParticipant,
          payload.status_id,
        )
        setSubParticipant(currentPitcher, 'participant', throwPitcherPayload)

        queue.addIncident(throwPitcherPayload)
        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.Ball]: {
      addIncident: (payload, queue, metadata) => {
        const { participant, currentBatter, currentPitcher } =
          getAddIncidentContext(metadata, 'pitching', payload)

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'participant', payload)

        const throwPitcherPayload = getThrowPitcherPayload(
          participant,
          payload.status_id,
        )
        setSubParticipant(currentPitcher, 'participant', throwPitcherPayload)

        queue.addIncident(throwPitcherPayload)
        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.Strike]: {
      addIncident: (payload, queue, metadata) => {
        const {
          participant,
          oppositeParticipant,
          currentBatter,
          currentPitcher,
        } = getAddIncidentContext(metadata, 'batting', payload)

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'participant', payload)

        const throwPitcherPayload = getThrowPitcherPayload(
          oppositeParticipant,
          payload.status_id,
        )
        setSubParticipant(currentPitcher, 'participant', throwPitcherPayload)

        queue.addIncident(throwPitcherPayload)
        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.SwingAndMiss]: {
      addIncident: (payload, queue, metadata) => {
        const {
          participant,
          oppositeParticipant,
          currentBatter,
          currentPitcher,
        } = getAddIncidentContext(metadata, 'batting', payload)

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'participant', payload)

        const throwPitcherPayload = getThrowPitcherPayload(
          oppositeParticipant,
          payload.status_id,
        )
        setSubParticipant(currentPitcher, 'participant', throwPitcherPayload)

        queue.addIncident(throwPitcherPayload)
        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.Foul]: {
      addIncident: (payload, queue, metadata) => {
        const {
          participant,
          oppositeParticipant,
          currentBatter,
          currentPitcher,
        } = getAddIncidentContext(metadata, 'batting', payload)

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'participant', payload)

        const throwPitcherPayload = getThrowPitcherPayload(
          oppositeParticipant,
          payload.status_id,
        )
        setSubParticipant(currentPitcher, 'participant', throwPitcherPayload)

        queue.addIncident(throwPitcherPayload)
        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.PickOffAttempt]: {
      addIncident: (payload, queue, metadata) => {
        const { participant, currentPitcher } = getAddIncidentContext(
          metadata,
          'pitching',
          payload,
        )

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentPitcher, 'participant', payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.Walk]: {
      addIncident: (payload, queue, metadata) => {
        const { participant, currentBatter } = getAddIncidentContext(
          metadata,
          'batting',
          payload,
        )

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'participant', payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.InPlay]: {
      addIncident: (payload, queue, metadata) => {
        const { participant } = getAddIncidentContext(
          metadata,
          'batting',
          payload,
        )

        setParticipantTeamId(participant, payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.InPlayOut]: {
      addIncident: (payload, queue, metadata) => {
        const { participant } = getAddIncidentContext(
          metadata,
          'batting',
          payload,
        )

        setParticipantTeamId(participant, payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.InPlayRun]: {
      addIncident: (payload, queue, metadata) => {
        const { participant } = getAddIncidentContext(
          metadata,
          'batting',
          payload,
        )

        setParticipantTeamId(participant, payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.TriplePlay]: {
      addIncident: (payload, queue, metadata) => {
        const { participant } = getAddIncidentContext(
          metadata,
          'pitching',
          payload,
        )

        setParticipantTeamId(participant, payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.WildPitch]: {
      addIncident: (payload, queue, metadata) => {
        const { participant, currentPitcher } = getAddIncidentContext(
          metadata,
          'pitching',
          payload,
        )

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentPitcher, 'participant', payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.StolenBase]: {
      addIncident: (payload, queue, metadata) => {
        const { participant } = getAddIncidentContext(
          metadata,
          'batting',
          payload,
        )

        setParticipantTeamId(participant, payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.CaughtStealing]: {
      addIncident: (payload, queue, metadata) => {
        const { participant } = getAddIncidentContext(
          metadata,
          'batting',
          payload,
        )

        setParticipantTeamId(participant, payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.HitByPitch]: {
      addIncident: (payload, queue, metadata) => {
        const {
          participant,
          oppositeParticipant,
          currentBatter,
          currentPitcher,
        } = getAddIncidentContext(metadata, 'batting', payload)

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'participant', payload)

        const throwPitcherPayload = getThrowPitcherPayload(
          oppositeParticipant,
          payload.status_id,
        )
        setSubParticipant(currentPitcher, 'participant', throwPitcherPayload)

        queue.addIncident(throwPitcherPayload)
        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.DoublePlay]: {
      addIncident: (payload, queue, metadata) => {
        const { participant } = getAddIncidentContext(
          metadata,
          'pitching',
          payload,
        )

        setParticipantTeamId(participant, payload)

        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.Error]: {
      addIncident: (payload, queue, metadata) => {
        const { participant, currentPitcher } = getAddIncidentContext(
          metadata,
          'pitching',
          payload,
        )

        setParticipantTeamId(participant, payload)

        const throwPitcherPayload = getThrowPitcherPayload(
          participant,
          payload.status_id,
        )
        setSubParticipant(currentPitcher, 'participant', throwPitcherPayload)

        queue.addIncident(throwPitcherPayload)
        queue.addIncident(payload)
      },
    },
    [BaseballIncidentId.PlateAppearance]: {
      addIncident: (payload, queue, metadata) => {
        const { participant, currentBatter } = getAddIncidentContext(
          metadata,
          'batting',
          payload,
        )

        setParticipantTeamId(participant, payload)
        setSubParticipant(currentBatter, 'participant', payload)

        queue.addIncident(payload)
      },
    },
  }
